import { useMemo } from "react";
import { FEATURE_FLAGS, MerklClaimTransaction } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useSendTransactionWithConnection } from "@/hooks/core/useSendTransactionWithConnection";
import { isWalletActionError, useWalletActionGuard, WALLET_ACTION_ERROR_CODE } from "@/hooks/core/useWalletActionGuard";
import { isWalletUserRejectedError } from "@/hooks/core/walletInteractionErrors";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useDebugOverrideAddress } from "@/hooks/user/useDebugOverrideAddress";
import { useHubUser } from "@/hooks/user/useHubUser";
import { isEVMAddress } from "@/utils/address";
import * as Sentry from "@sentry/nextjs";
import { skipToken, useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { formatUnits, getAddress } from "viem";
import { useConfig, useSwitchChain } from "wagmi";
import { estimateGas, waitForTransactionReceipt } from "wagmi/actions";

const MERKL_CLAIMABLE_QUERY_KEY = "hubRewardsMerklClaimable";
const MERKL_SUBMITTED_CLAIM_QUERY_KEY = "hubRewardsMerklSubmittedClaim";
const MERKL_CLAIM_MUTATION_KEY = "hubRewardsMerklClaim";

const merklSubmittedClaimQueryKey = (
  userAddress: string | undefined,
  chainId: number | undefined,
  rewardTokenSymbol: string | undefined,
) => [MERKL_SUBMITTED_CLAIM_QUERY_KEY, userAddress, chainId, rewardTokenSymbol];

const merklSubmittedClaimStorageKey = (userAddress: string, chainId: number, rewardTokenSymbol: string) =>
  `cede-merkl-claimed-${userAddress.toLowerCase()}-${chainId}-${rewardTokenSymbol}`;

export type MerklCampaignRewardDetails = {
  campaignId: string;
  campaignTitle: string;
  rewardTokenSymbol: string;
  claimable: Decimal;
  claimableSoon: Decimal;
};

const fromBaseUnits = (amount: string, decimals: number) => new Decimal(formatUnits(BigInt(amount), decimals));

type UseMerklRewardsProps = {
  onClaimSuccess?: () => void;
  onClaimError?: (error: Error) => void;
};

export const useMerklRewards = ({ onClaimSuccess, onClaimError }: UseMerklRewardsProps = {}) => {
  const apiHelper = useApi();
  const queryClient = useQueryClient();
  const wagmiConfig = useConfig();
  const { hubUser } = useHubUser();
  const userAddress = useDebugOverrideAddress();
  const isClaimsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.CLAIMS_HUB_ENABLE);
  const { prepareMainEvmAction } = useWalletActionGuard();
  const { switchChainAsync } = useSwitchChain();
  const { sendTransactionAsync } = useSendTransactionWithConnection();

  const { data, isLoading, isError } = useQuery({
    queryKey: [MERKL_CLAIMABLE_QUERY_KEY, hubUser?.id, userAddress],
    queryFn: () =>
      apiHelper.getOrThrow(API_ENDPOINT.HUB_REWARDS_MERKL_CLAIMABLE, {
        queryParams: { defiAddress: userAddress as string },
      }),
    enabled: isClaimsEnabled && !!userAddress && isEVMAddress(userAddress),
  });

  // The API guarantees every Cede Merkl campaign pays the same token, the one the Distributor claim pays.
  const claimToken = data?.campaigns[0];

  // Merkl's API lags the chain by minutes: until it sees the claim we sent, it still offers it. Keyed on the
  // cumulative amount, which survives new roots (their proofs differ) and only grows with new rewards.
  // Kept in localStorage, like the vault claim cooldown, so it survives reloads and new tabs.
  const submittedClaimChainId = data?.claimTransaction?.chainId;
  const { data: submittedClaimAmount } = useQuery<string | null>({
    queryKey: merklSubmittedClaimQueryKey(userAddress, submittedClaimChainId, claimToken?.rewardTokenSymbol),
    queryFn:
      userAddress && submittedClaimChainId != null && claimToken
        ? () =>
            localStorage.getItem(
              merklSubmittedClaimStorageKey(userAddress, submittedClaimChainId, claimToken.rewardTokenSymbol),
            )
        : skipToken,
    staleTime: Infinity,
  });

  const isAwaitingMerklIndex =
    submittedClaimAmount != null && data?.claimTransaction?.cumulativeAmount === submittedClaimAmount;
  const claimTransaction = isAwaitingMerklIndex ? null : (data?.claimTransaction ?? null);

  const campaigns: MerklCampaignRewardDetails[] = useMemo(
    () =>
      (data?.campaigns ?? [])
        .map((campaign) => ({
          campaignId: campaign.campaignId,
          campaignTitle: campaign.campaignTitle,
          rewardTokenSymbol: campaign.rewardTokenSymbol,
          claimable: fromBaseUnits(campaign.claimable, campaign.rewardTokenDecimals),
          claimableSoon: fromBaseUnits(campaign.claimableSoon, campaign.rewardTokenDecimals),
        }))
        .filter((campaign) => campaign.claimable.gt(0) || campaign.claimableSoon.gt(0))
        .map((campaign) => (isAwaitingMerklIndex ? { ...campaign, claimable: new Decimal(0) } : campaign)),
    [data?.campaigns, isAwaitingMerklIndex],
  );

  const totalClaimable = useMemo(
    () => campaigns.reduce((acc, campaign) => acc.plus(campaign.claimable), new Decimal(0)),
    [campaigns],
  );

  const walletClaimableAmount =
    claimTransaction && claimToken
      ? fromBaseUnits(claimTransaction.walletClaimableAmount, claimToken.rewardTokenDecimals)
      : null;
  // The claim pays `userAddress`, but the connected wallet signs it.
  const isImpersonating = userAddress?.toLowerCase() !== hubUser?.defiAddress?.toLowerCase();

  const claimMutationKey = [MERKL_CLAIM_MUTATION_KEY, userAddress];
  // Counted from the mutation cache, not the hook: a claim sent before the modal closed is still pending when it reopens.
  const isClaiming = useIsMutating({ mutationKey: claimMutationKey }) > 0;
  const { mutate: claim } = useMutation({
    mutationKey: claimMutationKey,
    mutationFn: async (claimTransaction: MerklClaimTransaction) => {
      const chain = wagmiConfig.chains.find((c) => c.id === claimTransaction.chainId);
      if (!chain) throw new Error(`Merkl claim chain ${claimTransaction.chainId} is not configured`);

      const { chainId: connectedChainId } = await prepareMainEvmAction();
      if (connectedChainId !== chain.id) {
        await switchChainAsync({ chainId: chain.id });
      }

      const to = getAddress(claimTransaction.to);
      const txData = claimTransaction.data as `0x${string}`;
      // Own estimate + 20% buffer: wallets that can't simulate fall back to a too-low gas limit.
      const gasEstimate = await estimateGas(wagmiConfig, {
        chainId: chain.id,
        account: getAddress(userAddress as string),
        to,
        data: txData,
      });
      const hash = await sendTransactionAsync({
        chainId: chain.id,
        to,
        data: txData,
        gas: (gasEstimate * 120n) / 100n,
      });
      const receipt = await waitForTransactionReceipt(wagmiConfig, { chainId: chain.id, hash });
      if (receipt.status !== "success") {
        throw new Error("The Merkl claim transaction reverted on-chain. Your rewards were not claimed.");
      }
    },
    onSuccess: (_, claimTransaction) => {
      if (userAddress && claimToken) {
        localStorage.setItem(
          merklSubmittedClaimStorageKey(userAddress, claimTransaction.chainId, claimToken.rewardTokenSymbol),
          claimTransaction.cumulativeAmount,
        );
      }
      queryClient.setQueryData(
        merklSubmittedClaimQueryKey(userAddress, claimTransaction.chainId, claimToken?.rewardTokenSymbol),
        claimTransaction.cumulativeAmount,
      );
      queryClient.invalidateQueries({ queryKey: [MERKL_CLAIMABLE_QUERY_KEY, hubUser?.id] });
      onClaimSuccess?.();
    },
    onError: (error) => {
      const isUserRejection =
        isWalletUserRejectedError(error) ||
        (isWalletActionError(error) && error.code === WALLET_ACTION_ERROR_CODE.USER_REJECTED);
      if (isUserRejection) return;
      Sentry.captureException(error, {
        level: "error",
        tags: { feature: "rewards-claim-merkl" },
        extra: { userAddress, hubUserId: hubUser?.id, claimTransaction: data?.claimTransaction },
      });
      onClaimError?.(error);
    },
  });

  return {
    isEnabled: isClaimsEnabled,
    isLoading,
    isError,
    campaigns,
    claimToken: claimToken ? { symbol: claimToken.rewardTokenSymbol } : null,
    totalClaimable,
    walletClaimableAmount,
    claimTransaction,
    isAwaitingMerklIndex,
    isImpersonating,
    claim,
    isClaiming,
  };
};
